import { IFlowDestinationListItem } from '_/features/flowDestinations/flowDestinations.types';
import { IFlowDestination, IFlowSegment, IFlowSegmentSettings } from '_/features/flows/common/types/flowSegments.types';
import { ISettings } from '_/types/settings.types';
import zeusSdk from '_/utils/SDKs';

export const getFlowSegment = async (flowSegmentId: string, params = {}): Promise<IFlowSegment> => {
  return (await zeusSdk.flowSegments.get(flowSegmentId, params)) as IFlowSegment;
};

export const createFlowSegmentSettings = async (
  flowSegmentId: string,
  data: Partial<IFlowSegmentSettings> = {},
): Promise<ISettings<IFlowSegmentSettings>> => {
  try {
    return (await zeusSdk.flowSegments.addSettings(flowSegmentId, {
      data: data,
    })) as ISettings<IFlowSegmentSettings>;
  } catch (error: any) {
    if (error?.status === 409) {
      // A settings row already exists for this segment -- e.g. another settings section was
      // unlinked first (all sections share one row), or an earlier attempt left one behind.
      // Fall back to updating it in place with our seed data rather than leaving the caller
      // stuck on a create that can never succeed.
      return updateFlowSegmentSettings(flowSegmentId, data);
    }
    throw error;
  }
};

export const deleteFlowSegmentSettings = async (flowSegmentId: string): Promise<void> => {
  await zeusSdk.flowSegments.deleteSettings(flowSegmentId);
};

export const updateFlowSegmentSettings = async (
  flowSegmentId: string,
  data: Partial<IFlowSegmentSettings>,
): Promise<ISettings<IFlowSegmentSettings>> => {
  return (await zeusSdk.flowSegments.updateSettings(flowSegmentId, {
    data: data,
  })) as ISettings<IFlowSegmentSettings>;
};

export const getDestinations = async (flowSegmentId: string, params = {}): Promise<IFlowDestination[]> => {
  const { items } = await zeusSdk.flowSegments.getChildren(flowSegmentId, 'destinations', params);
  return items as IFlowDestination[];
};

export const getDestinationsBySegmentsIds = async (segmentsIds: string[], params = {}): Promise<IFlowDestinationListItem[]> => {
  const promises = segmentsIds.map((id) =>
    getDestinations(id, params).then((destinations) => ({ id: id, destinations: destinations })),
  );
  return Promise.all(promises);
};

export const updateOverrideDestinations = async (
  id: string,
  overrideDestinations: boolean,
  params = {},
): Promise<IFlowSegment> => {
  return (await zeusSdk.flowSegments.patch(id, { overrideDestinations: overrideDestinations }, params)) as IFlowSegment;
};

export const linkDestination = async (flowSegmentId: string, destinationId: string, params = {}): Promise<IFlowDestination> => {
  return zeusSdk.flowSegments.linkChild(flowSegmentId, 'destinations', destinationId, params);
};

export const unlinkDestination = async (
  flowSegmentId: string,
  flowDestinationId: string,
  params = {},
): Promise<IFlowDestination> => {
  return zeusSdk.flowSegments.unlinkChild(flowSegmentId, 'destinations', flowDestinationId, params);
};
